import type { LinkedStoryResult } from '@repo/shared/orpc/procedures/spaces';
import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { Banner } from 'component/banner';
import { Pad, VertBox } from 'component/basics';
import { InformationalModal } from 'component/modal/informational-modal';
import { LinkText, UtilityText } from 'component/text';
import { Image, View } from 'react-native';
import { t } from 'util/stdtypes';

function LinkedPartnerStoryCard({ article }: { article: LinkedStoryResult }) {
    const s = useLinkedPartnerStoryCardStyle();
    const { colors } = useTheme();
    return (
        <View style={s.container}>
            {article.imageUrl && <Image source={{ uri: article.imageUrl }} style={s.image} />}
            <View style={{ flex: 1 }}>
                <UtilityText color={colors.colorTextSecondary} numberOfLines={1} text={article.title} weight='medium' />
                <Pad size={4} />
                <LinkText
                    label={
                        article.type === 'video'
                            ? t('linked_partner_story.view_video_link', 'View video')
                            : t('linked_partner_story.read_article_link', 'Read article')
                    }
                    type='small'
                    url={article.url}
                    weight='medium'
                />
            </View>
        </View>
    );
}

const useLinkedPartnerStoryCardStyle = createThemedStyles((tokens) => ({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        padding: 12,
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        borderRadius: 8,
        height: 72,
    },
    image: {
        width: 48,
        height: 48,
        borderRadius: 8,
    },
}));

export function LinkedPartnerStoriesModal({ articles, onClose }: { articles: LinkedStoryResult[]; onClose: () => void }) {
    const { colors } = useTheme();
    return (
        <InformationalModal
            content={
                <>
                    {articles.length > 0 ? (
                        <>
                            <Banner color={colors.colorSurfaceBannerInfo}>
                                <UtilityText
                                    color={colors.colorTextSecondaryOnBanner}
                                    label={t('linked_partner_story.incomplete_list_banner', 'This list of stories may be incomplete.')}
                                />
                            </Banner>
                            <VertBox gap={8} wide>
                                {articles.map((article) => (
                                    <LinkedPartnerStoryCard article={article} key={article.url} />
                                ))}
                            </VertBox>
                        </>
                    ) : (
                        <Banner color={colors.colorSurfaceSecondary}>
                            <UtilityText
                                color={colors.colorTextSecondaryOnBanner}
                                label={t('linked_partner_story.no_articles_found', 'There are no stories linked to this space yet.')}
                            />
                        </Banner>
                    )}
                </>
            }
            headingLabel={t('linked_partner_story.linked_articles_heading', 'Linked stories')}
            onClose={onClose}
            subheadingLabel={t('linked_partner_story.linked_articles_description', 'View stories attached to this conversation.')}
        />
    );
}
